const TRACE_STATUSES = new Set([
    "effective",
    "overridden",
    "refuted",
    "inactiveCondition",
]);
const CONTENT_HASH_ALGORITHMS = new Set(["blake3", "sha256"]);
const DIGEST_PATTERN = /^[0-9a-f]{64}$/;
const OPERATION_INDEXES = new WeakSet();

function isRecord(value) {
    return Boolean(value)
        && typeof value === "object"
        && !Array.isArray(value)
        && Object.prototype.toString.call(value) === "[object Object]";
}

function fail(path, message) {
    throw new CalculatorProjectionOperationIndexError(path, message);
}

function requireRecord(value, path) {
    if (!isRecord(value)) {
        fail(path, "expected an object");
    }
    return value;
}

function requireExactFields(value, fields, path) {
    const object = requireRecord(value, path);
    const expected = [...fields].sort();
    const actual = Object.keys(object).sort();
    if (
        actual.length !== expected.length
        || actual.some((field, index) => field !== expected[index])
    ) {
        fail(path, `fields must be exactly: ${expected.join(", ")}`);
    }
    return object;
}

function requireString(value, path) {
    if (typeof value !== "string" || !value || value.trim() !== value) {
        fail(path, "expected a non-empty string without surrounding whitespace");
    }
    return value;
}

function requireArray(value, path) {
    if (!Array.isArray(value)) {
        fail(path, "expected an array");
    }
    return value;
}

function parseContentHash(value, path) {
    if (value === null) {
        return null;
    }
    const hash = requireExactFields(value, ["algorithm", "digest"], path);
    if (!CONTENT_HASH_ALGORITHMS.has(hash.algorithm)) {
        fail(`${path}.algorithm`, "unsupported content hash algorithm");
    }
    if (typeof hash.digest !== "string" || !DIGEST_PATTERN.test(hash.digest)) {
        fail(`${path}.digest`, "expected a lowercase 64-character hexadecimal digest");
    }
    return Object.freeze({ algorithm: hash.algorithm, digest: hash.digest });
}

function contentHashKey(value) {
    return value === null ? null : `${value.algorithm}:${value.digest}`;
}

function traceLayer(layer, kind, path) {
    const value = requireRecord(layer, path);
    return Object.freeze({
        layerId: requireString(value.layerId, `${path}.layerId`),
        name: requireString(value.name, `${path}.name`),
        order: value.order,
        kind,
        evidenceClass: requireString(value.evidenceClass, `${path}.evidenceClass`),
    });
}

function addLayerOperations(
    entries,
    operationPaths,
    layer,
    kind,
    operations,
    path,
    operationsField = "operations",
) {
    const layerReference = traceLayer(layer, kind, path);
    requireArray(operations, `${path}.${operationsField}`).forEach((operation, index) => {
        const operationPath = `${path}.${operationsField}[${index}]`;
        const object = requireRecord(operation, operationPath);
        const operationId = requireString(object.operationId, `${operationPath}.operationId`);
        if (entries.has(operationId)) {
            fail(
                `${operationPath}.operationId`,
                `duplicates ${operationPaths.get(operationId)}`,
            );
        }
        entries.set(operationId, Object.freeze({
            operationId,
            layer: layerReference,
            operation: object,
        }));
        operationPaths.set(operationId, `${operationPath}.operationId`);
    });
}

function requireOperationIndex(value) {
    if (!OPERATION_INDEXES.has(value)) {
        throw new TypeError("operationIndex must come from buildCalculatorProjectionOperationIndex");
    }
    return value;
}

export class CalculatorProjectionOperationIndexError extends TypeError {
    constructor(path, message) {
        super(`calculator projection operation index failed at ${path}: ${message}`);
        this.name = "CalculatorProjectionOperationIndexError";
        this.path = path;
    }
}

/**
 * Retains the immutable model operations and the exact active v4 user-layer
 * operations behind their stable operation IDs. The index is rebuilt only when
 * either owner changes, never for a scenario-only evaluation.
 */
export function buildCalculatorProjectionOperationIndex(model, userLayer = null) {
    const modelValue = requireRecord(model, "$.model");
    const modelRevision = requireString(modelValue.modelRevision, "$.model.modelRevision");
    const entries = new Map();
    const operationPaths = new Map();
    const base = requireRecord(modelValue.baseFacts, "$.model.baseFacts");
    addLayerOperations(
        entries,
        operationPaths,
        base,
        "base",
        base.facts,
        "$.model.baseFacts",
        "facts",
    );
    requireArray(modelValue.sourceLayers, "$.model.sourceLayers").forEach((layer, index) => {
        const path = `$.model.sourceLayers[${index}]`;
        const value = requireRecord(layer, path);
        addLayerOperations(entries, operationPaths, value, "source", value.operations, path);
    });

    let userLayerContentHash = null;
    if (userLayer !== null) {
        const value = requireRecord(userLayer, "$.userLayer");
        if (value.evidenceClass !== "user") {
            fail("$.userLayer.evidenceClass", "expected user");
        }
        const provenance = requireRecord(value.provenance, "$.userLayer.provenance");
        userLayerContentHash = parseContentHash(
            provenance.contentHash,
            "$.userLayer.provenance.contentHash",
        );
        addLayerOperations(
            entries,
            operationPaths,
            value,
            "user",
            value.operations,
            "$.userLayer",
        );
    }

    const index = Object.freeze({
        modelRevision,
        userLayerContentHash,
        resolve(operationId, path = "$.operationId") {
            const id = requireString(operationId, path);
            const entry = entries.get(id);
            if (!entry) {
                fail(path, `unknown operation ID ${id}`);
            }
            return entry;
        },
        get size() {
            return entries.size;
        },
    });
    OPERATION_INDEXES.add(index);
    return index;
}

export function assertCalculatorProjectionOperationBinding(
    binding,
    operationIndex,
    path = "$.projection",
) {
    const index = requireOperationIndex(operationIndex);
    const value = requireRecord(binding, path);
    if (value.modelRevision !== index.modelRevision) {
        fail(`${path}.modelRevision`, "does not match the indexed model revision");
    }
    if (!Object.prototype.hasOwnProperty.call(value, "userLayerContentHash")) {
        fail(`${path}.userLayerContentHash`, "missing field");
    }
    const userLayerContentHash = parseContentHash(
        value.userLayerContentHash,
        `${path}.userLayerContentHash`,
    );
    if (contentHashKey(userLayerContentHash) !== contentHashKey(index.userLayerContentHash)) {
        fail(
            `${path}.userLayerContentHash`,
            "does not match the active v4 user layer",
        );
    }
    return userLayerContentHash;
}

/**
 * Resolves compact projection references into the layer/operation records used
 * by presentation. The compact wire object is not mutated.
 */
export function hydrateCalculatorProjectionTraceCandidates(projection, operationIndex) {
    const index = requireOperationIndex(operationIndex);
    const value = requireRecord(projection, "$.projection");
    const projectionHash = assertCalculatorProjectionOperationBinding(value, index);

    const traceIds = new Map();
    const candidateReferences = new Map();
    const hydratedTraces = requireArray(
        value.provenanceTraces,
        "$.projection.provenanceTraces",
    ).map((trace, traceIndex) => {
        const tracePath = `$.projection.provenanceTraces[${traceIndex}]`;
        const traceValue = requireRecord(trace, tracePath);
        const traceId = requireString(traceValue.traceId, `${tracePath}.traceId`);
        if (traceIds.has(traceId)) {
            fail(`${tracePath}.traceId`, `duplicates ${traceIds.get(traceId)}`);
        }
        traceIds.set(traceId, `${tracePath}.traceId`);
        const candidates = requireArray(
            traceValue.candidates,
            `${tracePath}.candidates`,
        ).map((candidate, candidateIndex) => {
            const candidatePath = `${tracePath}.candidates[${candidateIndex}]`;
            const candidateValue = requireExactFields(
                candidate,
                ["operationId", "status", "deactivatedByOperationIds"],
                candidatePath,
            );
            const operationId = requireString(
                candidateValue.operationId,
                `${candidatePath}.operationId`,
            );
            if (candidateReferences.has(operationId)) {
                fail(
                    `${candidatePath}.operationId`,
                    `duplicates ${candidateReferences.get(operationId)}`,
                );
            }
            candidateReferences.set(operationId, `${candidatePath}.operationId`);
            if (!TRACE_STATUSES.has(candidateValue.status)) {
                fail(`${candidatePath}.status`, "unsupported trace candidate status");
            }
            const entry = index.resolve(operationId, `${candidatePath}.operationId`);
            const deactivatorIds = new Set();
            const deactivators = [];
            const deactivatedByOperationIds = requireArray(
                candidateValue.deactivatedByOperationIds,
                `${candidatePath}.deactivatedByOperationIds`,
            ).map((deactivatorId, deactivatorIndex) => {
                const deactivatorPath = `${candidatePath}.deactivatedByOperationIds[${deactivatorIndex}]`;
                const id = requireString(deactivatorId, deactivatorPath);
                if (deactivatorIds.has(id)) {
                    fail(deactivatorPath, `duplicates deactivator ${id}`);
                }
                deactivatorIds.add(id);
                const deactivator = index.resolve(id, deactivatorPath);
                if (deactivator.operation.kind !== "refuteEvidence") {
                    fail(deactivatorPath, "does not reference a refuteEvidence operation");
                }
                deactivators.push(Object.freeze({
                    layer: deactivator.layer,
                    operation: deactivator.operation,
                }));
                return id;
            });
            return Object.freeze({
                layer: entry.layer,
                operation: entry.operation,
                status: candidateValue.status,
                deactivatedByOperationIds: Object.freeze(deactivatedByOperationIds),
                deactivators: Object.freeze(deactivators),
            });
        });
        return Object.freeze({
            ...traceValue,
            candidates: Object.freeze(candidates),
        });
    });

    return Object.freeze({
        ...value,
        userLayerContentHash: projectionHash,
        provenanceTraces: Object.freeze(hydratedTraces),
    });
}
